import type { DateTime } from "luxon";
import type { CandidateStatus } from "../../components/UIToolkit/Badges/CandidateStatusChip";

/**
 * Frontend types for the Hiring Hub API. Mirrors `backend/models/hiring_hub.py`.
 *
 * WIRE CASING (verified against the passing backend API tests):
 *  - GET RESPONSES are **snake_case** — the response models (`HubCandidateRow`, `HubProcess`, …) declare
 *    no aliases and there is no `alias_generator` on `InHouseBaseModel`, so FastAPI (`response_model_by_alias`)
 *    emits the field names verbatim. So we mirror snake_case here, exactly like the v0 chat types do.
 *  - REQUEST bodies / query params use **camelCase** for the aliased fields (`firstName`, `departmentId`,
 *    `appliedDate`, `interviewerUserId`, `pipelineStatus`, `noInterviewer`, …) and plain names for the rest
 *    (`email`, `phone`, `position`, `wage`, `shift`, `notes`).
 *
 * The axios response interceptor (`convertIsoDates`) turns ISO date/datetime strings into Luxon `DateTime`,
 * so every `*_date`, `*_at` field arrives as `DateTime`. `interview_time` is a bare `"HH:MM:SS"` string
 * (no date part → not converted). `wage` is a numeric value (FastAPI encodes `Decimal` as a JSON number).
 */

export enum CandidatePipelineStatus {
	NEW = "NEW",
	SCREENED = "SCREENED",
	INTERVIEWED = "INTERVIEWED",
	OFFERED = "OFFERED",
	HIRED = "HIRED",
	ACCEPTED_NEVER_STARTED = "ACCEPTED_NEVER_STARTED",
	DISQUALIFIED = "DISQUALIFIED",
}

export enum InterviewShift {
	DAY = "DAY",
	NIGHT = "NIGHT",
	BOTH = "BOTH",
}

/** The two candidate document slots. Path values are exactly these strings (backend `DocumentType`). */
export type HubDocType = "cv" | "certificate";

// --------------------------------------------------------------------------- //
// Grid (GET /candidates) + counts
// --------------------------------------------------------------------------- //
/** One grid row = one candidate_process, with its most-recent interview folded in. */
export type HubCandidateRow = {
	process_id: string;
	candidate_id: string;
	candidate_name: string;
	phone: string;
	email: string | null;
	department_id: string;
	facility_name: string;
	position: string | null;
	wage: number | null;
	shift: string | null;
	pipeline_status: CandidatePipelineStatus;
	applied_date: DateTime | null;
	hire_date: DateTime | null;
	notes: string | null;
	disqualification_reason: string | null;
	never_started_reason: string | null;
	background_date: DateTime | null;
	onboarding_date: DateTime | null;
	orientation_date: DateTime | null;
	cv_file_name: string | null;
	certificate_file_name: string | null;
	interview_id: string | null;
	interview_date: DateTime | null;
	interview_time: string | null;
	interview_status: string | null;
	interviewer_user_id: string | null;
	interviewer_name: string | null;
	interviewer_phone: string | null;
	created_at: DateTime;
};

export type HubCandidatesResponse = {
	candidates: HubCandidateRow[];
	total: number;
};

export type HubPipelineCount = {
	pipeline_status: CandidatePipelineStatus;
	count: number;
};

export type HubCandidateCountsResponse = {
	counts: HubPipelineCount[];
	total: number;
};

// --------------------------------------------------------------------------- //
// Profile (GET /candidates/{id})
// --------------------------------------------------------------------------- //
export type HubInterview = {
	id: string;
	interview_date: DateTime | null;
	interview_time: string | null;
	status: CandidateStatus | null;
	cancel_reason: string | null;
	interviewer_user_id: string | null;
	interviewer_name: string | null;
	feedback: string | null;
	created_at: DateTime;
};

/** One note on a candidate_process. `author_name` is null for notes migrated from the legacy single-textarea
 *  notes column (no author was ever recorded) and for a note whose author's user row was since deleted. */
export type HubProcessNote = {
	id: string;
	title: string;
	body: string;
	created_by_user_id: string | null;
	author_name: string | null;
	created_at: DateTime;
	updated_at: DateTime;
};

export type HubProcess = {
	id: string;
	department_id: string;
	facility_name: string;
	position: string | null;
	wage: number | null;
	shift: InterviewShift | null;
	applied_date: DateTime | null;
	hire_date: DateTime | null;
	pipeline_status: CandidatePipelineStatus;
	pipeline_status_changed_at: DateTime | null;
	disqualification_reason: string | null;
	never_started_reason: string | null;
	notes: string | null;
	background_date: DateTime | null;
	onboarding_date: DateTime | null;
	orientation_date: DateTime | null;
	interviews: HubInterview[];
	/** The profile's note trail, newest first. */
	process_notes: HubProcessNote[];
};

export type HubDocument = {
	file_name: string;
	uploaded_by_user_id: string | null;
	uploaded_at: DateTime | null;
};

export type HubCandidateDocuments = {
	cv: HubDocument | null;
	certificate: HubDocument | null;
};

export type HubCandidateProfile = {
	id: string;
	first_name: string;
	last_name: string | null;
	email: string | null;
	phone: string;
	documents: HubCandidateDocuments;
	processes: HubProcess[];
};

// --------------------------------------------------------------------------- //
// Interviewer / recruiter management
// --------------------------------------------------------------------------- //
export type HubDepartmentUser = {
	user_id: string;
	/** First name — the linked nurse's, else user.first_name. */
	first_name: string | null;
	/** Last name from the linked nurse record (null when the user isn't a nurse), for the full display name. */
	last_name: string | null;
};

/** One dated interview for the Interview Schedule calendar. Dates/times are ISO strings from the backend. */
export type HubScheduleEntry = {
	interview_id: string;
	candidate_id: string;
	candidate_name: string;
	interviewer_name: string | null;
	department_id: string;
	department_name: string;
	/** Luxon DateTime (the axios interceptor converts the ISO date), or null when not scheduled yet. */
	interview_date: DateTime | null;
	/** "HH:MM:SS" or null (not scheduled to a time; not converted — it isn't an ISO date). */
	interview_time: string | null;
	status: string | null;
};

export type HubScheduleResponse = {
	entries: HubScheduleEntry[];
};

/** The caller's hiring-role summary (GET /hiring-chat/access) — gates the recruiter-only Hub surfaces. */
export type HubAccess = {
	/** Recruiter-anywhere or support → full Hub (Interviewers tab). Interviewer-only → false. */
	sees_all: boolean;
	is_support: boolean;
	is_recruiter: boolean;
	is_interviewer: boolean;
};

/** Whether the caller gets the Hiring tab (GET /hiring-chat/tab-access) — the `hiring_tab` flag OR a hiring
 * role. Un-gated, so a non-hiring user gets `can_access: false` rather than a 403; drives the nav + route. */
export type HubTabAccess = {
	can_access: boolean;
};

export type HubDepartmentUsersResponse = {
	users: HubDepartmentUser[];
};

/**
 * One searchable interviewer candidate for the Add Interviewer picker (an existing department member).
 * `GET /hiring-chat/interviewer-candidates` returns a bare array of these (snake_case, no wrapper).
 * `last_name` is always null (the `user` table has only `first_name`); `department_ids` are the departments
 * the person belongs to, used to constrain the modal's Department dropdown when a nurse is picked first.
 */
export type InterviewerCandidate = {
	user_id: string;
	first_name: string | null;
	last_name: string | null;
	email: string | null;
	phone: string | null;
	department_ids: string[];
	/** The grantable departments WITH names — the Department dropdown is built from these once a person is picked. */
	departments: { id: string; display_name: string }[];
};

/** One interviewer-roster card (Interviewers tab): the person + a department they interview in + contact info. */
export type HubInterviewerRosterEntry = {
	user_id: string;
	first_name: string | null;
	last_name: string | null;
	email: string | null;
	phone: string | null;
	department_id: string;
	department_name: string;
};

export type HubInterviewersResponse = {
	interviewers: HubInterviewerRosterEntry[];
};

/** One candidate an interviewer is currently assigned to in a department (delete-interviewer confirmation). */
export type HubInterviewerAssignment = {
	candidate_id: string;
	candidate_name: string;
};

export type HubInterviewerAssignmentsResponse = {
	assignments: HubInterviewerAssignment[];
};

/** One department the caller may toggle a person's interviewer role in, with the current flag (Edit modal). */
export type HubEditableDepartment = {
	id: string;
	display_name: string;
	is_interviewer: boolean;
};

export type HubEditableDepartmentsResponse = {
	departments: HubEditableDepartment[];
};

export type CreatedInterviewResponse = {
	id: string;
};

export type DocumentUrlResponse = {
	url: string;
};

// --------------------------------------------------------------------------- //
// Request bodies (camelCase — see WIRE CASING note above)
// --------------------------------------------------------------------------- //
export type CreateCandidateRequest = {
	firstName: string;
	lastName?: string | null;
	email?: string | null;
	phone: string;
	departmentId: string;
	position?: string | null;
	wage?: number | null;
	shift?: InterviewShift | null;
	appliedDate?: string | null;
	hireDate?: string | null;
	notes?: string | null;
	interviewDate?: string | null;
	interviewTime?: string | null;
	interviewerUserId?: string | null;
};

export type UpdateCandidateRequest = {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	// Backend normalizes the number and returns 409 `hiring_chat.candidate.phone_taken` on collision.
	phone?: string | null;
};

export type UpdateProcessRequest = {
	position?: string | null;
	wage?: number | null;
	shift?: InterviewShift | null;
	appliedDate?: string | null;
	hireDate?: string | null;
	notes?: string | null;
	// Move this process to another department. Backend returns 409
	// `hiring_chat.process.department_conflict` when the candidate already has a process there.
	departmentId?: string | null;
	disqualificationReason?: string | null;
	neverStartedReason?: string | null;
	// ISO date strings (or null to clear) — the onboarding-step dates, like appliedDate / hireDate.
	backgroundDate?: string | null;
	onboardingDate?: string | null;
	orientationDate?: string | null;
};

/** Create/edit a candidate-profile note. Both fields are required and non-blank — the backend strips and
 *  rejects an empty title or body (422). */
export type ProcessNoteRequest = {
	title: string;
	body: string;
};

export type UpdatePipelineStatusRequest = {
	pipelineStatus: CandidatePipelineStatus;
	disqualificationReason?: string | null;
	neverStartedReason?: string | null;
};

export type UpdateInterviewRequest = {
	interviewDate?: string | null;
	interviewTime?: string | null;
	interviewerUserId?: string | null;
	feedback?: string | null;
};

/** Filters driving GET /candidates (query params, camelCase aliases). */
export type HubCandidateFilters = {
	departmentId?: string | null;
	// Multi-department filter — serialized as a repeatable `departmentIds` query param. The single
	// `departmentId` above stays supported; when both are present the backend unions them.
	departmentIds?: string[] | null;
	pipelineStatus?: CandidatePipelineStatus | null;
	position?: string | null;
	noInterviewer?: boolean;
	q?: string | null;
};

/** The two pipeline statuses that require a reason before the transition can be confirmed. */
export const PIPELINE_STATUS_REASON_REQUIRED: ReadonlySet<CandidatePipelineStatus> = new Set([
	CandidatePipelineStatus.DISQUALIFIED,
	CandidatePipelineStatus.ACCEPTED_NEVER_STARTED,
]);

/** Ordered funnel stages, for the grid Status column, the count bar, and the status-change modal. */
export const PIPELINE_STATUS_ORDER: readonly CandidatePipelineStatus[] = [
	CandidatePipelineStatus.NEW,
	CandidatePipelineStatus.SCREENED,
	CandidatePipelineStatus.INTERVIEWED,
	CandidatePipelineStatus.OFFERED,
	CandidatePipelineStatus.HIRED,
	CandidatePipelineStatus.ACCEPTED_NEVER_STARTED,
	CandidatePipelineStatus.DISQUALIFIED,
];
